// src/client.ts
import axios from "axios";
var _client = null;
function initApiClient(options) {
  _client = axios.create({
    baseURL: options.baseURL,
    withCredentials: options.withCredentials ?? true,
    timeout: options.timeout ?? 15e3,
    headers: { "Content-Type": "application/json" }
  });
}
function getClient() {
  if (!_client) {
    throw new Error(
      "API client not initialized. Call initApiClient({ baseURL }) before making any API calls."
    );
  }
  return _client;
}
function setClient(instance) {
  _client = instance;
}

// src/endpoints/auth.api.ts
var authApi = {
  register: (dto) => getClient().post("/auth/register", dto),
  login: (dto) => getClient().post("/auth/login", dto),
  adminLogin: (dto) => getClient().post("/auth/admin/login", dto),
  portalLogin: (dto) => getClient().post("/auth/portal/login", dto),
  logout: () => getClient().post("/auth/logout"),
  // Web: uses httpOnly cookie (no body needed)
  refresh: () => getClient().post("/auth/refresh"),
  // Mobile: sends refresh token in body (no httpOnly cookie on native apps)
  refreshMobile: (dto) => getClient().post("/auth/refresh/mobile", dto),
  sendOtp: (dto) => getClient().post("/auth/send-otp", dto),
  verifyOtp: (dto) => getClient().post("/auth/verify-otp", dto),
  registerDriver: (dto) => getClient().post("/auth/register-driver", dto),
  forgotPassword: (dto) => getClient().post("/auth/forgot-password", dto),
  resetPassword: (dto) => getClient().post("/auth/reset-password", dto),
  getMe: () => getClient().get("/users/me"),
  addRole: (role) => getClient().post("/auth/add-role", { role }),
  verifyAndAddRole: (dto) => getClient().post("/auth/verify-and-add-role", dto)
};

// src/endpoints/users.api.ts
var usersApi = {
  getProfile: () => getClient().get("/users/me"),
  updateProfile: (dto) => getClient().patch("/users/me", dto),
  addAddress: (dto) => getClient().post("/users/me/addresses", dto),
  updateAddress: (addressId, dto) => getClient().patch(`/users/me/addresses/${addressId}`, dto),
  deleteAddress: (addressId) => getClient().delete(`/users/me/addresses/${addressId}`),
  setDefaultAddress: (addressId) => getClient().patch(`/users/me/addresses/${addressId}/default`),
  updatePushToken: (dto) => getClient().patch("/users/me/push-token", dto),
  saveWebPushSubscription: (sub) => getClient().post("/users/me/web-push-subscription", sub),
  removeWebPushSubscription: (endpoint) => getClient().delete("/users/me/web-push-subscription", { data: { endpoint } }),
  // ── Favorites ───────────────────────────────────────────────────
  listFavorites: () => getClient().get("/users/me/favorites"),
  addFavorite: (restaurantId) => getClient().post(`/users/me/favorites/${restaurantId}`),
  removeFavorite: (restaurantId) => getClient().delete(`/users/me/favorites/${restaurantId}`),
  // S14-10: menu-item favorites (mirrors restaurant favorites)
  listFavoriteItems: () => getClient().get("/users/me/favorite-items"),
  addFavoriteItem: (menuItemId) => getClient().post(`/users/me/favorite-items/${menuItemId}`),
  removeFavoriteItem: (menuItemId) => getClient().delete(`/users/me/favorite-items/${menuItemId}`),
  exportData: () => getClient().get("/users/me/data-export"),
  deleteAccount: () => getClient().delete("/users/me/account")
};

// src/endpoints/restaurants.api.ts
var restaurantsApi = {
  getAll: (params) => getClient().get("/restaurants", { params }),
  getById: (id) => getClient().get(`/restaurants/${id}`),
  getNearby: (params) => getClient().get("/restaurants", { params }),
  search: (q, params) => getClient().get("/search", {
    params: { q, ...params }
  })
};

// src/endpoints/menu.api.ts
var menuApi = {
  getCategories: (restaurantId) => getClient().get(`/restaurants/${restaurantId}/categories`),
  createCategory: (restaurantId, dto) => getClient().post(`/restaurants/${restaurantId}/categories`, dto),
  updateCategory: (restaurantId, id, dto) => getClient().patch(`/restaurants/${restaurantId}/categories/${id}`, dto),
  deleteCategory: (restaurantId, id) => getClient().delete(`/restaurants/${restaurantId}/categories/${id}`),
  getItems: (restaurantId) => getClient().get(`/restaurants/${restaurantId}/menu`),
  getItemById: (_restaurantId, itemId) => getClient().get(`/menu-items/${itemId}`)
};

// src/endpoints/orders.api.ts
import { OrderStatus } from "@grandxl/types";
var ordersApi = {
  // Pass an `idempotencyKey` to dedupe network retries — same key + same body returns
  // the original response instead of creating a second order.
  create: (dto, idempotencyKey) => getClient().post(
    "/orders",
    dto,
    idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : void 0
  ),
  getById: (id) => getClient().get(`/orders/my/${id}`),
  getHistory: (params) => getClient().get("/orders/my", { params }),
  updateStatus: (id, dto) => getClient().patch(`/orders/${id}/status`, dto),
  editScheduled: (id, dto) => getClient().patch(`/orders/my/${id}`, dto),
  cancel: (id, reason) => getClient().patch(`/orders/${id}/status`, {
    status: OrderStatus.CANCELLED,
    cancelReason: reason
  }),
  getRiderContact: (id) => getClient().get(`/orders/${id}/rider-contact`),
  getCustomerContact: (id) => getClient().get(`/orders/${id}/customer-contact`)
};

// src/endpoints/payments.api.ts
var paymentsApi = {
  // POST /payments/initiate — gets Paystack authorization URL for an order
  initiate: (dto) => getClient().post("/payments/initiate", dto),
  // GET /payments/verify/:reference — poll payment status after redirect
  verify: (reference) => getClient().get(`/payments/verify/${reference}`)
};

// src/endpoints/riders.api.ts
var ridersApi = {
  getProfile: () => getClient().get("/riders/me"),
  registerRider: (dto) => getClient().post("/riders/register", dto),
  toggleOnline: (isOnline) => getClient().patch("/riders/me/availability", { isOnline }),
  // S15-9: break / pause mode. Rider stays online (GPS + session live) but
  // dispatcher skips them. Pass false to resume.
  setBreak: (onBreak) => getClient().patch("/riders/me/break", { onBreak }),
  updateLocation: (dto) => getClient().patch("/riders/me/location", dto),
  updateDocuments: (dto) => getClient().patch("/riders/me/documents", dto),
  getAvailableJobs: () => getClient().get("/riders/me/jobs/available"),
  getActiveJob: () => getClient().get("/riders/me/jobs/active"),
  acceptJob: (orderId) => getClient().post(`/riders/me/jobs/${orderId}/accept`),
  declineJob: (orderId, reason) => getClient().post(`/riders/me/jobs/${orderId}/decline`, reason ? { reason } : {}),
  getDeliveryHistory: (params) => getClient().get("/riders/me/jobs/history", { params }),
  getMetrics: (sinceDays) => getClient().get("/riders/me/metrics", { params: sinceDays ? { sinceDays } : void 0 })
};

// src/endpoints/reviews.api.ts
var reviewsApi = {
  create: (dto) => getClient().post("/reviews", dto),
  getByRestaurant: (restaurantId, params) => getClient().get(`/restaurants/${restaurantId}/reviews`, {
    params
  })
};

// src/endpoints/uploads.api.ts
function toFormData(file) {
  if (file instanceof FormData) return file;
  const fd = new FormData();
  fd.append("file", file);
  return fd;
}
var uploadsApi = {
  uploadAvatar: (file) => getClient().post(
    "/uploads/avatar",
    toFormData(file),
    { headers: { "Content-Type": "multipart/form-data" } }
  ),
  uploadRestaurantCover: (file) => getClient().post(
    "/uploads/restaurant-cover",
    toFormData(file),
    { headers: { "Content-Type": "multipart/form-data" } }
  ),
  uploadRestaurantLogo: (file) => getClient().post(
    "/uploads/restaurant-logo",
    toFormData(file),
    { headers: { "Content-Type": "multipart/form-data" } }
  ),
  uploadMenuItemPhoto: (file) => getClient().post(
    "/uploads/menu-item",
    toFormData(file),
    { headers: { "Content-Type": "multipart/form-data" } }
  ),
  uploadRiderDocument: (file) => getClient().post(
    "/uploads/rider-document",
    toFormData(file),
    { headers: { "Content-Type": "multipart/form-data" } }
  ),
  uploadDeliveryProof: (file) => getClient().post(
    "/uploads/delivery-proof",
    toFormData(file),
    { headers: { "Content-Type": "multipart/form-data" } }
  ),
  // Keep old name as alias so existing callers don't break
  uploadImage: (file) => getClient().post(
    "/uploads/avatar",
    toFormData(file),
    { headers: { "Content-Type": "multipart/form-data" } }
  )
};

// src/endpoints/wallet.api.ts
var walletApi = {
  getBalance: () => getClient().get("/wallet"),
  // Idempotent: pass an Idempotency-Key header to dedupe network retries.
  // Returns accessCode so the caller can open PaystackPop.resumeTransaction()
  // for the same inline-popup pattern used by order payments.
  topUp: (dto, idempotencyKey) => getClient().post(
    "/wallet/topup",
    dto,
    idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : void 0
  ),
  getTransactions: (params) => getClient().get("/wallet/transactions", { params })
};

// src/endpoints/coupons.api.ts
var couponsApi = {
  validate: (dto) => getClient().post("/coupons/validate", dto)
};
var restaurantCouponsApi = {
  create: (dto) => getClient().post("/restaurant/coupons", dto),
  list: () => getClient().get("/restaurant/coupons"),
  deactivate: (id) => getClient().delete(`/restaurant/coupons/${id}`)
};

// src/endpoints/admin.api.ts
var adminRestaurantsApi = {
  list: (params) => getClient().get("/admin/restaurants", { params }),
  getById: (id) => getClient().get(`/admin/restaurants/${id}`),
  onboard: (dto) => getClient().post("/admin/restaurants", dto),
  approve: (id) => getClient().patch(`/admin/restaurants/${id}/approve`),
  reject: (id, dto) => getClient().patch(`/admin/restaurants/${id}/reject`, dto),
  requestInfo: (id, dto) => getClient().patch(`/admin/restaurants/${id}/request-info`, dto),
  suspend: (id, dto) => getClient().patch(`/admin/restaurants/${id}/suspend`, dto),
  reinstate: (id) => getClient().patch(`/admin/restaurants/${id}/reinstate`),
  terminate: (id, dto) => getClient().patch(`/admin/restaurants/${id}/terminate`, dto),
  transferOwnership: (id, dto) => getClient().patch(`/admin/restaurants/${id}/transfer-ownership`, dto)
};
var adminRidersApi = {
  list: (params) => getClient().get("/admin/riders", { params }),
  getById: (id) => getClient().get(`/admin/riders/${id}`),
  onboard: (dto) => getClient().post("/admin/riders", dto),
  verify: (id) => getClient().post(`/admin/riders/${id}/verify`),
  // Sprint 13 (S13-7): reject KYC with a specific reason — rider gets a
  // push and re-uploads. Verified riders can't be KYC-rejected (use suspend).
  rejectKyc: (id, reason) => getClient().post(`/admin/riders/${id}/reject-kyc`, { reason }),
  suspend: (id, dto) => getClient().patch(`/admin/riders/${id}/suspend`, dto),
  reinstate: (id) => getClient().patch(`/admin/riders/${id}/reinstate`),
  terminate: (id, dto) => getClient().patch(`/admin/riders/${id}/terminate`, dto),
  assignToOrder: (riderId, orderId) => getClient().post(
    `/admin/riders/${riderId}/assign/${orderId}`
  )
};
var adminOrdersApi = {
  list: (params) => getClient().get("/admin/orders", { params }),
  getById: (id) => getClient().get(`/admin/orders/${id}`),
  clearAll: () => getClient().delete("/admin/orders/all"),
  redispatch: (id) => getClient().post(`/admin/orders/${id}/redispatch`),
  dispatchDebug: (id) => getClient().get(`/admin/orders/${id}/dispatch-debug`),
  // Sprint 13 (S13-4): manual rider reassignment
  reassignCandidates: (id) => getClient().get(`/admin/orders/${id}/reassign-candidates`),
  reassignRider: (id, riderId, reason) => getClient().post(`/admin/orders/${id}/reassign-rider`, { riderId, reason }),
  bulkCancel: (dto) => getClient().post("/admin/orders/bulk-cancel", dto),
  opsHealth: () => getClient().get("/admin/orders/ops/health")
};
var adminAuditApi = {
  list: (params) => getClient().get("/admin/audit-logs", { params })
};
var adminReviewsApi = {
  getFlagged: (params) => getClient().get("/admin/reviews/flagged", { params }),
  setVisibility: (id, dto) => getClient().patch(`/admin/reviews/${id}/visibility`, dto)
};
var platformConfigApi = {
  get: () => getClient().get("/platform/config"),
  update: (dto) => getClient().patch("/platform/config", dto),
  listCoupons: (params) => getClient().get("/platform/coupons", { params }),
  createCoupon: (dto) => getClient().post("/platform/coupons", dto),
  deactivateCoupon: (id) => getClient().delete(`/platform/coupons/${id}`)
};
var adminUsersApi = {
  list: (params) => getClient().get("/admin/users", { params }),
  create: (dto) => getClient().post("/admin/users", dto),
  ban: (id, reason) => getClient().patch(`/admin/users/${id}/ban`, { reason }),
  unban: (id) => getClient().patch(`/admin/users/${id}/unban`),
  delete: (id) => getClient().delete(`/admin/users/${id}`),
  clearAllRiskFlags: (id) => getClient().patch(`/admin/users/${id}/risk-flags/clear`),
  clearRiskFlag: (id, code) => getClient().patch(
    `/admin/users/${id}/risk-flags/${encodeURIComponent(code)}/clear`
  )
};
var adminBlocklistApi = {
  listCustomers: (params) => getClient().get("/admin/blocklist/customers", { params }),
  listRiders: (params) => getClient().get("/admin/blocklist/riders", { params })
};
var adminDeliveryZonesApi = {
  list: () => getClient().get("/admin/delivery-zones"),
  create: (dto) => getClient().post("/admin/delivery-zones", dto),
  update: (id, dto) => getClient().patch(`/admin/delivery-zones/${id}`, dto),
  delete: (id) => getClient().delete(`/admin/delivery-zones/${id}`)
};
var adminSurgePricingApi = {
  list: () => getClient().get("/admin/surge-pricing"),
  currentMultiplier: () => getClient().get("/admin/surge-pricing/current-multiplier"),
  create: (dto) => getClient().post("/admin/surge-pricing", dto),
  update: (id, dto) => getClient().patch(`/admin/surge-pricing/${id}`, dto),
  delete: (id) => getClient().delete(`/admin/surge-pricing/${id}`)
};
var adminFraudApi = {
  listFlagged: (params) => getClient().get("/admin/fraud/flagged-users", { params })
};
var adminSupportApi = {
  forceRefund: (dto) => getClient().post("/admin/support/force-refund", dto),
  emergencyCredit: (dto) => getClient().post("/admin/support/emergency-credit", dto),
  customerLookup: (q) => getClient().get("/admin/support/customer-lookup", { params: { q } }),
  customerOverview: (userId) => getClient().get(`/admin/support/customer/${userId}/overview`),
  contactCustomer: (dto) => getClient().post("/admin/support/contact-customer", dto)
};
var adminBroadcastsApi = {
  send: (dto) => getClient().post("/admin/broadcasts", dto),
  list: (params) => getClient().get("/admin/broadcasts", { params })
};
var myRestaurantApi = {
  list: () => getClient().get("/restaurants/my/restaurants"),
  create: (dto) => getClient().post("/restaurants", dto),
  getById: (id) => getClient().get(`/restaurants/${id}`),
  update: (id, dto) => getClient().patch(`/restaurants/${id}`, dto),
  submitForReview: (id) => getClient().patch(`/restaurants/${id}/submit-for-review`),
  getOrders: (restaurantId, params) => getClient().get(`/orders/restaurant/${restaurantId}`, { params }),
  getOrderById: (orderId) => getClient().get(`/orders/${orderId}`),
  clearOrderHistory: (restaurantId) => getClient().delete(`/orders/restaurant/${restaurantId}/history`)
};
var menuManagementApi = {
  createCategory: (restaurantId, dto) => getClient().post(`/restaurants/${restaurantId}/categories`, dto),
  updateCategory: (restaurantId, id, dto) => getClient().patch(`/restaurants/${restaurantId}/categories/${id}`, dto),
  deleteCategory: (restaurantId, id) => getClient().delete(`/restaurants/${restaurantId}/categories/${id}`),
  createItem: (restaurantId, dto) => getClient().post(`/restaurants/${restaurantId}/menu-items`, dto),
  updateItem: (restaurantId, id, dto) => getClient().patch(`/restaurants/${restaurantId}/menu-items/${id}`, dto),
  deleteItem: (restaurantId, id) => getClient().delete(`/restaurants/${restaurantId}/menu-items/${id}`),
  // ── Sprint 12 (S12-7): bulk edit ─────────────────────────────────────
  bulkSetAvailability: (restaurantId, itemIds, isAvailable) => getClient().post(
    `/restaurants/${restaurantId}/menu-items/bulk-availability`,
    { itemIds, isAvailable }
  ),
  bulkSetFeatured: (restaurantId, itemIds, isFeatured) => getClient().post(
    `/restaurants/${restaurantId}/menu-items/bulk-featured`,
    { itemIds, isFeatured }
  ),
  bulkMoveCategory: (restaurantId, itemIds, categoryId) => getClient().post(
    `/restaurants/${restaurantId}/menu-items/bulk-category`,
    { itemIds, categoryId }
  ),
  bulkAdjustPrice: (restaurantId, itemIds, mode, value) => getClient().post(
    `/restaurants/${restaurantId}/menu-items/bulk-price`,
    { itemIds, mode, value }
  ),
  bulkDelete: (restaurantId, itemIds) => getClient().post(
    `/restaurants/${restaurantId}/menu-items/bulk-delete`,
    { itemIds }
  )
};
var analyticsApi = {
  getPlatform: () => getClient().get("/admin/analytics"),
  getRestaurant: (restaurantId) => getClient().get("/restaurant/analytics", {
    params: { restaurantId }
  }),
  getRestaurantFinancialReport: (restaurantId, from, to) => getClient().get("/restaurant/financial-report", {
    params: { restaurantId, from, to }
  }),
  getDispatchMetrics: (days = 7) => getClient().get("/admin/analytics/dispatch", {
    params: { days }
  }),
  getQueueDepth: () => getClient().get("/admin/analytics/queue-depth"),
  getHeatmap: (days = 30) => getClient().get("/admin/analytics/heatmap", {
    params: { days }
  }),
  getOrderTimeouts: (days = 7) => getClient().get("/admin/analytics/order-timeouts", {
    params: { days }
  }),
  getRestaurantEngagement: (days = 30) => getClient().get("/admin/analytics/restaurant-engagement", {
    params: { days }
  }),
  getRestaurantWaitTimes: (days = 30) => getClient().get("/admin/analytics/restaurant-wait-times", {
    params: { days }
  }),
  getRiderUtilization: (days = 7) => getClient().get("/admin/analytics/rider-utilization", {
    params: { days }
  })
};

// src/endpoints/food-categories.api.ts
var foodCategoriesApi = {
  getAll: () => getClient().get("/food-categories"),
  create: (dto) => getClient().post("/food-categories", dto),
  update: (id, dto) => getClient().patch(`/food-categories/${id}`, dto),
  remove: (id) => getClient().delete(`/food-categories/${id}`)
};

// src/endpoints/notifications.api.ts
var notificationsApi = {
  getMine: (params) => getClient().get("/notifications/my", { params }),
  markAsRead: (id) => getClient().patch(`/notifications/${id}/read`),
  markAllRead: () => getClient().patch("/notifications/read-all"),
  getVapidPublicKey: () => getClient().get("/notifications/vapid-public-key")
};

// src/endpoints/payouts.api.ts
var riderPayoutsApi = {
  getBankAccount: () => getClient().get("/rider/payouts/bank-account"),
  updateBankAccount: (dto) => getClient().put("/rider/payouts/bank-account", dto),
  getBanks: () => getClient().get("/rider/payouts/banks"),
  verifyAccount: (accountNumber, bankCode) => getClient().post("/rider/payouts/verify-account", {
    accountNumber,
    bankCode
  }),
  request: (amountKobo, idempotencyKey) => getClient().post("/rider/payouts", { amountKobo }, {
    headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : void 0
  }),
  list: (params) => getClient().get("/rider/payouts", { params })
};
var restaurantPayoutsApi = {
  getBankAccount: () => getClient().get("/restaurant/payouts/bank-account"),
  updateBankAccount: (dto) => getClient().put("/restaurant/payouts/bank-account", dto),
  getBanks: () => getClient().get("/restaurant/payouts/banks"),
  verifyAccount: (accountNumber, bankCode) => getClient().post("/restaurant/payouts/verify-account", {
    accountNumber,
    bankCode
  }),
  getSummary: () => getClient().get("/restaurant/payouts/summary"),
  request: (amountKobo, idempotencyKey) => getClient().post("/restaurant/payouts", { amountKobo }, {
    headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : void 0
  }),
  list: (params) => getClient().get("/restaurant/payouts", { params })
};
var adminPayoutsApi = {
  list: (params) => getClient().get("/admin/payouts", { params }),
  decide: (id, dto) => getClient().patch(`/admin/payouts/${id}/decide`, dto),
  // Sprint 13 (S13-9): batch-approve. Server iterates via the single-approve
  // logic; failures on individual payouts are returned per-id so the client
  // can toast partial results and highlight which rows still need attention.
  batchApprove: (payoutIds, note) => getClient().post("/admin/payouts/batch-approve", {
    payoutIds,
    note
  })
};

// src/endpoints/refunds.api.ts
var refundsApi = {
  create: (dto) => getClient().post("/refunds", dto),
  list: (params) => getClient().get("/refunds/my", { params })
};
var adminRefundsApi = {
  list: (params) => getClient().get("/admin/refunds", { params }),
  decide: (id, dto) => getClient().patch(`/admin/refunds/${id}/decide`, dto)
};

// src/endpoints/chat.api.ts
var chatApi = {
  getMessages: (orderId, params) => getClient().get(`/chat/${orderId}/messages`, { params }),
  markRead: (orderId) => getClient().post(`/chat/${orderId}/read`),
  deleteMessage: (orderId, messageId) => getClient().delete(`/chat/${orderId}/messages/${messageId}`)
};

// src/endpoints/platform.api.ts
var platformApi = {
  getPricing: () => getClient().get("/platform/pricing")
};

// src/endpoints/search.api.ts
var searchApi = {
  search: (params) => getClient().get("/search", { params }),
  autocomplete: (q, country) => getClient().get("/search/autocomplete", {
    params: { q, ...country ? { country } : {} }
  })
};

// src/endpoints/disputes.api.ts
var disputesApi = {
  // Customer
  create: (payload) => getClient().post("/disputes", payload),
  listMine: (params) => getClient().get("/disputes/my", { params }),
  // Admin
  listAll: (params) => getClient().get("/disputes", { params }),
  resolve: (id, resolution) => getClient().patch(`/disputes/${id}/resolve`, { resolution })
};

// src/endpoints/referrals.api.ts
var referralsApi = {
  getMyInfo: () => getClient().get("/referrals/me"),
  applyCode: (code) => getClient().post("/referrals/apply", { code }),
  getAdminOverview: (days = 30) => getClient().get("/admin/referrals/overview", {
    params: { days }
  })
};

// src/endpoints/maps.api.ts
var mapsApi = {
  // Reverse-geocode a lat/lng pair. Requires an authed session (super-admin
  // or authenticated user) — enforced by the API's global JwtAuthGuard.
  reverse: (lat, lng) => getClient().get("/maps/reverse", {
    params: { lat: String(lat), lng: String(lng) }
  }),
  // Address autocomplete. Public — called from signup / register flows before
  // the user has a session.
  placesAutocomplete: (q) => getClient().get("/maps/places/autocomplete", {
    params: { q }
  }),
  // Place details lookup for a place_id. Public for the same signup reason.
  placeDetails: (placeId) => getClient().get("/maps/places/details", {
    params: { placeId }
  }),
  // Forward-geocode a free-text address → coordinates. Public.
  geocode: (address) => getClient().get("/maps/geocode", {
    params: { address }
  })
};

// src/endpoints/reward-points.api.ts
var rewardPointsApi = {
  getBalance: () => getClient().get("/reward-points/me"),
  getHistory: (params) => getClient().get("/reward-points/me/history", { params })
};
export {
  adminAuditApi,
  adminBlocklistApi,
  adminBroadcastsApi,
  adminDeliveryZonesApi,
  adminFraudApi,
  adminOrdersApi,
  adminPayoutsApi,
  adminRefundsApi,
  adminRestaurantsApi,
  adminReviewsApi,
  adminRidersApi,
  adminSupportApi,
  adminSurgePricingApi,
  adminUsersApi,
  analyticsApi,
  authApi,
  chatApi,
  couponsApi,
  disputesApi,
  foodCategoriesApi,
  getClient,
  initApiClient,
  mapsApi,
  menuApi,
  menuManagementApi,
  myRestaurantApi,
  notificationsApi,
  ordersApi,
  paymentsApi,
  platformApi,
  platformConfigApi,
  referralsApi,
  refundsApi,
  restaurantCouponsApi,
  restaurantPayoutsApi,
  restaurantsApi,
  reviewsApi,
  rewardPointsApi,
  riderPayoutsApi,
  ridersApi,
  searchApi,
  setClient,
  uploadsApi,
  usersApi,
  walletApi
};
